import * as THREE from 'three';
import type { PlayerController } from '../player/PlayerController';
import type { WeaponId } from './WeaponDefs';

export type LauncherKind = 'gl' | 'rpg';

export interface SplashEvent {
  origin: THREE.Vector3;
  radius: number;
  damage: number;
  weaponId: WeaponId;
}

interface LiveProj {
  kind: LauncherKind;
  mesh: THREE.Object3D;
  vel: THREE.Vector3;
  life: number;
  age: number;
  bounces: number;
  led?: THREE.MeshBasicMaterial;
  blinkT: number;
  trailAcc: number;
  stopFire?: () => void;
}

const GRENADE_LIFE = 2.8;
const GRENADE_RADIUS = 5.8;
const ROCKET_RADIUS = 4.2;
const GRAVITY = 16.5;
const FUSE_PANIC = 0.55;
const SCORCH_MAX = 14;
const _scorchN = new THREE.Vector3();
const _scorchLook = new THREE.Vector3();
const _rocketNose = new THREE.Vector3(0, 0, -1);
const _rocketDir = new THREE.Vector3();
const SCORCH_DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [0, -1, 0],
  [0, 1, 0],
  [1, 0, 0],
  [-1, 0, 0],
  [0, 0, 1],
  [0, 0, -1],
  [0.7, -0.7, 0],
  [-0.7, -0.7, 0],
  [0, -0.7, 0.7],
  [0, -0.7, -0.7],
];

function aimRocket(mesh: THREE.Object3D, dir: THREE.Vector3): void {
  _rocketDir.copy(dir);
  if (_rocketDir.lengthSq() < 1e-8) return;
  _rocketDir.normalize();
  mesh.quaternion.setFromUnitVectors(_rocketNose, _rocketDir);
}

function isIgnored(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData?.ignoreHit || o.userData?.noHit) return true;
    o = o.parent;
  }
  return false;
}

export class ProjectileSystem {
  private readonly group = new THREE.Group();
  private readonly scorches = new THREE.Group();
  private readonly live: LiveProj[] = [];
  private readonly ray = new THREE.Raycaster();
  private readonly tmp = new THREE.Vector3();
  private readonly nrm = new THREE.Vector3();
  private onSplash: ((e: SplashEvent) => void) | null = null;
  private onBounce: ((at: THREE.Vector3, bounce: number) => void) | null = null;

  constructor(private readonly scene: THREE.Scene) {
    this.group.userData.ignoreHit = true;
    this.group.userData.noRoomCull = true;
    this.scorches.userData.ignoreHit = true;
    this.scorches.userData.noRoomCull = true;
    this.scene.add(this.group);
    this.scene.add(this.scorches);
  }

  setSplashHandler(handler: (e: SplashEvent) => void): void {
    this.onSplash = handler;
  }

  setBounceHandler(handler: (at: THREE.Vector3, bounce: number) => void): void {
    this.onBounce = handler;
  }

  spawn(kind: LauncherKind, origin: THREE.Vector3, dir: THREE.Vector3, stopFire?: () => void): void {
    const mesh = kind === 'gl' ? makeGrenade() : makeRocket();
    mesh.position.copy(origin);
    if (kind === 'rpg') aimRocket(mesh, dir);
    this.group.add(mesh);
    const speed = kind === 'gl' ? 17.5 : 38;
    this.live.push({
      kind,
      mesh,
      vel: dir.clone().normalize().multiplyScalar(speed),
      life: kind === 'gl' ? GRENADE_LIFE : 4,
      age: 0,
      bounces: 0,
      led: mesh.userData.ledMat as THREE.MeshBasicMaterial | undefined,
      blinkT: 0,
      trailAcc: 0,
      stopFire,
    });
  }

  update(dt: number, world: THREE.Object3D, player: PlayerController): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i]!;
      p.age += dt;
      p.life -= dt;
      if (p.kind === 'gl') p.vel.y -= GRAVITY * dt;

      const step = this.tmp.copy(p.vel).multiplyScalar(dt);
      const dist = step.length();
      if (dist > 1e-5) {
        this.ray.set(p.mesh.position, step.clone().normalize());
        this.ray.far = dist + 0.04;
        const hits = this.ray.intersectObject(world, true);
        let hit: THREE.Intersection | undefined;
        for (const h of hits) {
          if (isIgnored(h.object)) continue;
          if (h.object === p.mesh || p.mesh.children.includes(h.object)) continue;
          if (p.age < 0.06 && h.distance < 0.14) continue;
          hit = h;
          break;
        }
        if (hit) {
          const enemy = findEnemyId(hit.object) !== undefined;
          if (p.kind === 'rpg' || enemy) {
            this.explode(p, hit.point, world);
            this.live.splice(i, 1);
            continue;
          }
          if (hit.face) {
            this.nrm.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
          } else {
            this.nrm.set(0, 1, 0);
          }
          p.mesh.position.copy(hit.point).addScaledVector(this.nrm, 0.04);
          p.vel.reflect(this.nrm);
          p.vel.multiplyScalar(0.62);
          p.bounces += 1;
          this.onBounce?.(hit.point, p.bounces);
          if (p.bounces > 6) {
            this.explode(p, p.mesh.position, world);
            this.live.splice(i, 1);
            continue;
          }
        } else {
          p.mesh.position.add(step);
        }
      }

      if (p.kind === 'rpg') {
        aimRocket(p.mesh, p.vel);
        spawnTrail(this.group, p.mesh.position);
      } else {
        p.mesh.rotation.x += dt * 8;
        p.mesh.rotation.z += dt * 5;
        pulseGrenadeLed(p, dt);
        p.trailAcc += dt;
        if (p.trailAcc > 0.035) {
          p.trailAcc = 0;
          spawnGrenadeSmoke(this.group, p.mesh.position);
        }
      }

      const toPlayer = player.camera.position.distanceTo(p.mesh.position);
      if (p.age > 0.22 && toPlayer < 0.45) {
        this.explode(p, p.mesh.position, world);
        this.live.splice(i, 1);
        continue;
      }

      if (p.life <= 0) {
        this.explode(p, p.mesh.position, world);
        this.live.splice(i, 1);
      }
    }

    for (let i = this.group.children.length - 1; i >= 0; i--) {
      const c = this.group.children[i]!;
      if (c.userData.trailLife == null) continue;
      c.userData.trailLife -= dt;
      const vel = c.userData.trailVel as THREE.Vector3 | undefined;
      if (vel) {
        vel.y -= 14 * dt;
        c.position.addScaledVector(vel, dt);
      }
      const max = (c.userData.trailMax as number | undefined) ?? 0.28;
      const u = 1 - Math.max(0, c.userData.trailLife) / max;
      if (c.userData.trailGrow) {
        c.scale.setScalar((c.userData.trailGrow as number) * (0.25 + u * 1.4));
      } else {
        c.scale.setScalar(Math.max(0.01, c.userData.trailLife * 2));
      }
      const mat = (c as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (mat && 'opacity' in mat && c.userData.fade) {
        mat.opacity = Math.max(0, (1 - u) * (c.userData.baseOpacity ?? 0.7));
      }
      if (c.userData.trailLife <= 0) this.group.remove(c);
    }
  }

  private explode(p: LiveProj, at: THREE.Vector3, world: THREE.Object3D): void {
    p.stopFire?.();
    p.stopFire = undefined;
    this.group.remove(p.mesh);
    const radius = p.kind === 'gl' ? GRENADE_RADIUS : ROCKET_RADIUS;
    const damage = p.kind === 'gl' ? 85 : 130;
    this.onSplash?.({
      origin: at.clone(),
      radius,
      damage,
      weaponId: p.kind,
    });
    spawnBurst(this.group, at);
    this.stampScorch(world, at, radius);
  }

  private stampScorch(world: THREE.Object3D, at: THREE.Vector3, radius: number): void {
    const reach = radius * 0.72;
    this.ray.far = reach;
    for (const [dx, dy, dz] of SCORCH_DIRS) {
      this.tmp.set(dx, dy, dz).normalize();
      this.ray.set(at, this.tmp);
      const hits = this.ray.intersectObject(world, true);
      let hit: THREE.Intersection | undefined;
      for (const h of hits) {
        if (isIgnored(h.object)) continue;
        if (findEnemyId(h.object) !== undefined) continue;
        hit = h;
        break;
      }
      if (!hit || hit.distance > reach) continue;
      if (hit.face) {
        _scorchN.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
      } else {
        _scorchN.copy(this.tmp).multiplyScalar(-1);
      }
      if (_scorchN.dot(this.tmp) > 0) _scorchN.negate();
      const fall = 1 - hit.distance / reach;
      const size = (0.55 + fall * 1.65) * (dy < -0.4 ? 1.15 : 0.85);
      this.addScorch(hit.point, _scorchN, size);
    }
  }

  private addScorch(point: THREE.Vector3, normal: THREE.Vector3, size: number): void {
    const mesh = new THREE.Mesh(scorchGeo(), scorchMat());
    mesh.position.copy(point).addScaledVector(normal, 0.018);
    _scorchLook.copy(point).add(normal);
    mesh.lookAt(_scorchLook);
    mesh.scale.setScalar(size);
    mesh.userData.ignoreHit = true;
    this.scorches.add(mesh);
    while (this.scorches.children.length > SCORCH_MAX) {
      this.scorches.remove(this.scorches.children[0]!);
    }
  }
}

let _scorchGeo: THREE.CircleGeometry | null = null;
let _scorchMat: THREE.MeshBasicMaterial | null = null;

function scorchGeo(): THREE.CircleGeometry {
  if (!_scorchGeo) _scorchGeo = new THREE.CircleGeometry(1, 22);
  return _scorchGeo;
}

function scorchMat(): THREE.MeshBasicMaterial {
  if (_scorchMat) return _scorchMat;
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(64, 64, 6, 64, 64, 62);
  g.addColorStop(0, 'rgba(18,12,8,0.92)');
  g.addColorStop(0.28, 'rgba(42,28,16,0.78)');
  g.addColorStop(0.62, 'rgba(28,20,14,0.42)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 16; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 16 + Math.random() * 36;
    ctx.fillStyle = `rgba(10,8,6,${0.1 + Math.random() * 0.22})`;
    ctx.beginPath();
    ctx.ellipse(
      64 + Math.cos(a) * r * 0.4,
      64 + Math.sin(a) * r * 0.4,
      7 + Math.random() * 14,
      5 + Math.random() * 10,
      a,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  _scorchMat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    side: THREE.DoubleSide,
  });
  return _scorchMat;
}

function pulseGrenadeLed(p: LiveProj, dt: number): void {
  if (!p.led) return;
  p.blinkT += dt;
  const panic = p.life < FUSE_PANIC;
  const hz = panic ? 14 : 3.2;
  const on = Math.sin(p.blinkT * hz * Math.PI * 2) > (panic ? -0.15 : 0);
  p.led.color.setHex(on && panic ? 0xffee88 : on ? 0xff3310 : 0x4a1208);
}

function findEnemyId(obj: THREE.Object3D): number | undefined {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (typeof o.userData?.enemyId === 'number') return o.userData.enemyId as number;
    o = o.parent;
  }
  return undefined;
}

function makeGrenade(): THREE.Group {
  const g = new THREE.Group();
  g.userData.ignoreHit = true;
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.07, 0.16, 6, 10),
    new THREE.MeshStandardMaterial({
      color: 0x8a9284,
      roughness: 0.28,
      metalness: 0.82,
    }),
  );
  g.add(body);
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(0.074, 0.074, 0.022, 10),
    new THREE.MeshStandardMaterial({
      color: 0xe8ecf0,
      roughness: 0.16,
      metalness: 0.95,
    }),
  );
  g.add(band);
  const ledMat = new THREE.MeshBasicMaterial({ color: 0xff3310 });
  const led = new THREE.Mesh(new THREE.SphereGeometry(0.028, 8, 6), ledMat);
  led.position.y = 0.12;
  g.add(led);
  g.userData.ledMat = ledMat;
  return g;
}

/** Nose sits on local -Z so it matches the viewmodel tube and flight aim. */
export function createRocketMesh(opts?: { scale?: number; glow?: boolean }): THREE.Group {
  const s = opts?.scale ?? 1;
  const g = new THREE.Group();
  g.userData.ignoreHit = true;
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.055 * s, 0.062 * s, 0.42 * s, 8),
    new THREE.MeshBasicMaterial({ color: 0xd8b07a }),
  );
  body.rotation.x = Math.PI / 2;
  g.add(body);
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.062 * s, 0.14 * s, 8),
    new THREE.MeshBasicMaterial({ color: 0xe47a38 }),
  );
  nose.rotation.x = -Math.PI / 2;
  nose.position.z = -0.26 * s;
  g.add(nose);
  for (let i = 0; i < 4; i++) {
    const fin = new THREE.Mesh(
      new THREE.BoxGeometry(0.004 * s, 0.05 * s, 0.072 * s),
      new THREE.MeshBasicMaterial({ color: 0xc89858 }),
    );
    const a = (i / 4) * Math.PI * 2;
    fin.position.set(Math.cos(a) * 0.052 * s, Math.sin(a) * 0.052 * s, 0.15 * s);
    fin.rotation.z = a;
    g.add(fin);
  }
  if (opts?.glow !== false) {
    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(0.05 * s, 8, 6),
      new THREE.MeshBasicMaterial({
        color: 0xffaa55,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    glow.position.z = 0.24 * s;
    g.add(glow);
  }
  return g;
}

function makeRocket(): THREE.Group {
  return createRocketMesh({ scale: 1, glow: true });
}

function spawnTrail(parent: THREE.Object3D, at: THREE.Vector3): void {
  const s = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 6, 5),
    new THREE.MeshBasicMaterial({ color: 0xccaa88, transparent: true, opacity: 0.45 }),
  );
  s.position.copy(at);
  s.userData.trailLife = 0.28;
  s.userData.ignoreHit = true;
  parent.add(s);
}

function spawnGrenadeSmoke(parent: THREE.Object3D, at: THREE.Vector3): void {
  const s = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 6, 5),
    new THREE.MeshBasicMaterial({
      color: 0x8a8680,
      transparent: true,
      opacity: 0.38,
      depthWrite: false,
    }),
  );
  s.position.copy(at).add(
    new THREE.Vector3((Math.random() - 0.5) * 0.04, (Math.random() - 0.5) * 0.04, (Math.random() - 0.5) * 0.04),
  );
  s.userData.trailLife = 0.32 + Math.random() * 0.12;
  s.userData.trailMax = s.userData.trailLife;
  s.userData.trailGrow = 0.16;
  s.userData.fade = true;
  s.userData.baseOpacity = 0.38;
  s.userData.ignoreHit = true;
  parent.add(s);
}

function spawnBurst(parent: THREE.Object3D, at: THREE.Vector3): void {
  const flash = new THREE.Mesh(
    new THREE.SphereGeometry(0.55, 10, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffcc66,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
  );
  flash.position.copy(at);
  flash.userData.trailLife = 0.18;
  flash.userData.trailMax = 0.18;
  flash.userData.trailGrow = 1.4;
  flash.userData.fade = true;
  flash.userData.baseOpacity = 0.9;
  flash.userData.ignoreHit = true;
  parent.add(flash);

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.12, 0.28, 24),
    new THREE.MeshBasicMaterial({
      color: 0xffaa44,
      transparent: true,
      opacity: 0.75,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
  );
  ring.position.copy(at);
  ring.rotation.x = Math.PI / 2;
  ring.userData.trailLife = 0.28;
  ring.userData.trailMax = 0.28;
  ring.userData.trailGrow = 2.4;
  ring.userData.fade = true;
  ring.userData.baseOpacity = 0.75;
  ring.userData.ignoreHit = true;
  parent.add(ring);

  for (let i = 0; i < 16; i++) {
    const spark = new THREE.Mesh(
      new THREE.SphereGeometry(0.045, 5, 4),
      new THREE.MeshBasicMaterial({
        color: i % 3 === 0 ? 0xffee88 : 0xff6622,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    spark.position.copy(at);
    spark.userData.trailVel = new THREE.Vector3(
      (Math.random() - 0.5) * 8,
      2 + Math.random() * 6,
      (Math.random() - 0.5) * 8,
    );
    spark.userData.trailLife = 0.28 + Math.random() * 0.22;
    spark.userData.trailMax = spark.userData.trailLife;
    spark.userData.fade = true;
    spark.userData.baseOpacity = 0.85;
    spark.userData.ignoreHit = true;
    parent.add(spark);
  }

  for (let i = 0; i < 6; i++) {
    const puff = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 6, 5),
      new THREE.MeshBasicMaterial({
        color: 0x6a645c,
        transparent: true,
        opacity: 0.4,
        depthWrite: false,
      }),
    );
    puff.position.copy(at).add(
      new THREE.Vector3((Math.random() - 0.5) * 0.3, Math.random() * 0.25, (Math.random() - 0.5) * 0.3),
    );
    puff.userData.trailVel = new THREE.Vector3(
      (Math.random() - 0.5) * 1.2,
      0.6 + Math.random() * 1.1,
      (Math.random() - 0.5) * 1.2,
    );
    puff.userData.trailLife = 0.45 + Math.random() * 0.2;
    puff.userData.trailMax = puff.userData.trailLife;
    puff.userData.trailGrow = 0.55;
    puff.userData.fade = true;
    puff.userData.baseOpacity = 0.4;
    puff.userData.ignoreHit = true;
    parent.add(puff);
  }
}
